/**
 * useDashboardState
 *
 * Owns admin dashboard UI state that isn't tied to game/round/player data:
 * the active tab, the current full-screen overlay, and modal open flags.
 */

import { useCallback, useState } from 'react';
import type { GamePlayerInfo } from '@swtorbit/shared-socket-events';

export type DashboardTabId =
  | 'settings'
  | 'rounds'
  | 'teams'
  | 'results'
  | 'statistics'
  | 'effects'
  | 'cinematics'
  | 'notifications'
  | 'players'
  | 'discord';

/**
 * The full-screen overlays that replace the main dashboard body. Only one can
 * be open at a time, so they are modelled as a single value rather than
 * independent booleans (independent flags let two open at once, which made the
 * fixed-priority render "stick" on the first-opened view).
 */
export type DashboardOverlay =
  | 'games-list'
  | 'content-library'
  | 'global-data'
  | 'admin-management';

export interface DashboardState {
  activeTab: DashboardTabId;
  setActiveTab: (tab: DashboardTabId) => void;
  showGamesListView: boolean;
  setShowGamesListView: (val: boolean) => void;
  showContentLibraryView: boolean;
  setShowContentLibraryView: (val: boolean) => void;
  showGlobalDataView: boolean;
  setShowGlobalDataView: (val: boolean) => void;
  showAdminManagementView: boolean;
  setShowAdminManagementView: (val: boolean) => void;
  showCreateGameModal: boolean;
  setShowCreateGameModal: (val: boolean) => void;
  showDiscordAuthModal: boolean;
  setShowDiscordAuthModal: (val: boolean) => void;
  showSeedModal: boolean;
  setShowSeedModal: (val: boolean) => void;
  showProfileModal: boolean;
  setShowProfileModal: (val: boolean) => void;
  regModalPlayer: GamePlayerInfo | null;
  setRegModalPlayer: (player: GamePlayerInfo | null) => void;
}

export function useDashboardState(): DashboardState {
  const [activeTab, setActiveTab] = useState<DashboardTabId>('settings');
  // Single source of truth for the current overlay — guarantees mutual
  // exclusion so switching straight from one area to another always lands on
  // the newly-requested view.
  const [activeOverlay, setActiveOverlay] = useState<DashboardOverlay | null>(null);
  const [showCreateGameModal, setShowCreateGameModal] = useState(false);
  const [showDiscordAuthModal, setShowDiscordAuthModal] = useState(false);
  const [showSeedModal, setShowSeedModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [regModalPlayer, setRegModalPlayer] = useState<GamePlayerInfo | null>(null);

  // Setting an overlay's flag to `true` makes it the active overlay (closing
  // any other); setting it to `false` only closes it when it is the active one.
  const setOverlay = useCallback(
    (overlay: DashboardOverlay, val: boolean) =>
      setActiveOverlay((current) => (val ? overlay : current === overlay ? null : current)),
    [],
  );
  const setShowGamesListView = useCallback((val: boolean) => setOverlay('games-list', val), [setOverlay]);
  const setShowContentLibraryView = useCallback((val: boolean) => setOverlay('content-library', val), [setOverlay]);
  const setShowGlobalDataView = useCallback((val: boolean) => setOverlay('global-data', val), [setOverlay]);
  const setShowAdminManagementView = useCallback((val: boolean) => setOverlay('admin-management', val), [setOverlay]);

  return {
    activeTab,
    setActiveTab,
    showGamesListView: activeOverlay === 'games-list',
    setShowGamesListView,
    showContentLibraryView: activeOverlay === 'content-library',
    setShowContentLibraryView,
    showGlobalDataView: activeOverlay === 'global-data',
    setShowGlobalDataView,
    showAdminManagementView: activeOverlay === 'admin-management',
    setShowAdminManagementView,
    showCreateGameModal,
    setShowCreateGameModal,
    showDiscordAuthModal,
    setShowDiscordAuthModal,
    showSeedModal,
    setShowSeedModal,
    showProfileModal,
    setShowProfileModal,
    regModalPlayer,
    setRegModalPlayer,
  };
}
